import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Stack,
  Grid,
  Autocomplete,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  capitalize,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  RemoveRedEyeOutlined,
  Refresh,
  Diamond,
  FilterList,
  Close,
  Clear,
  ExpandMore,
  ExpandLess,
  LockOpenOutlined,
  GavelOutlined,
  SwapHorizOutlined,
  DeleteOutline,
  WarningAmberOutlined,
  ReplayOutlined,
  AttachFileOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import AccessDeniedBox from '../../../../components/AccessDeniedBox';
import usePermissions from '../../../../hooks/usePermissions';
import Selector from '../../../../components/Selector';
import { categoriasGarantia } from '../../../../utils/staticvariables';
import {
  EstadoChip,
  CategoryChip,
  SectionHeader,
  StatCard,
  CATEGORIA_META,
  garantiaLabel,
  camposDeCategoria,
} from './garantiasUI';
import GarantiaAdjuntos from './GarantiaAdjuntos';

const GarantiasManagement = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const puedeEditar = hasPermission('garantias', 'editar');
  const puedeEliminar = hasPermission('garantias', 'eliminar');
  const [garantiasData, setGarantiasData] = useState([]);
  const [garantiasFiltered, setGarantiasFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedGarantia, setSelectedGarantia] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [filterCategory, setFilterCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [clientes, setClientes] = useState([]);
  
  const [filters, setFilters] = useState({
    categoria: '',
    estado: '',
    sucursal: '',
    cliente: '',
    valorMin: '',
    valorMax: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  // Lifecycle state-change dialog
  const [estadoDialog, setEstadoDialog] = useState({ open: false, garantia: null, nuevoEstado: '' });
  const [motivo, setMotivo] = useState('');
  const [savingEstado, setSavingEstado] = useState(false);
  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Inline error for actions (does not replace the whole screen like `error`)
  const [actionError, setActionError] = useState(null);
  // Adjuntos (documentos / fotos)
  const [adjuntosTarget, setAdjuntosTarget] = useState(null);
  // Substitution form: create the replacement collateral and link it to the original
  const [sustituirDialog, setSustituirDialog] = useState({ open: false, garantia: null });
  const [sustCategoria, setSustCategoria] = useState('');
  const [sustCampos, setSustCampos] = useState({});
  const [sustMotivo, setSustMotivo] = useState('');
  const [sustErrors, setSustErrors] = useState({});
  const [savingSust, setSavingSust] = useState(false);

  const ESTADO_ACCIONES = {
    liberada: { label: 'Liberar', verbo: 'liberar', icon: <LockOpenOutlined fontSize="small" />, color: 'success' },
    ejecutada: { label: 'Ejecutar', verbo: 'ejecutar', icon: <GavelOutlined fontSize="small" />, color: 'error' },
    sustituida: { label: 'Sustituir', verbo: 'sustituir', icon: <SwapHorizOutlined fontSize="small" />, color: 'warning' },
    activa: { label: 'Reactivar', verbo: 'reactivar', icon: <ReplayOutlined fontSize="small" />, color: 'info' },
  };

  const openEstadoDialog = (garantia, nuevoEstado) => {
    setMotivo('');
    setEstadoDialog({ open: true, garantia, nuevoEstado });
  };

  const closeEstadoDialog = () => {
    if (savingEstado) return;
    setEstadoDialog({ open: false, garantia: null, nuevoEstado: '' });
    setMotivo('');
  };

  const submitEstado = async () => {
    if (!estadoDialog.garantia) return;
    setSavingEstado(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${estadoDialog.garantia.id}/estado`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          },
          body: JSON.stringify({ estado: estadoDialog.nuevoEstado, motivo })
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo cambiar el estado de la garantía');
      }
      setEstadoDialog({ open: false, garantia: null, nuevoEstado: '' });
      setMotivo('');
      fetchGarantias();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSavingEstado(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${deleteTarget.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo eliminar la garantía');
      }
      setDeleteTarget(null);
      fetchGarantias();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openSustituir = (garantia) => {
    setSustCategoria('');
    setSustCampos({});
    setSustMotivo('');
    setSustErrors({});
    setSustituirDialog({ open: true, garantia });
  };

  const closeSustituir = () => {
    if (savingSust) return;
    setSustituirDialog({ open: false, garantia: null });
  };

  const handleSustField = (campo, value) => {
    // Para montos guardamos solo el número (sin comas) para que valor sea limpio
    const clean = campo.type === 'monto' ? value.replace(/[^0-9.]/g, '') : value;
    setSustCampos(prev => ({ ...prev, [campo.id]: clean }));
    setSustErrors(prev => ({ ...prev, [campo.id]: undefined }));
  };

  const submitSustituir = async () => {
    if (!sustituirDialog.garantia) return;
    // Validar requeridos
    const errs = {};
    if (!sustCategoria) errs.categoria = 'Selecciona una categoría';
    camposDeCategoria(sustCategoria).forEach(campo => {
      if (campo.required && !sustCampos[campo.id]) {
        errs[campo.id] = `${campo.name} es requerido`;
      }
    });
    if (Object.keys(errs).length) {
      setSustErrors(errs);
      return;
    }
    setSavingSust(true);
    setActionError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${sustituirDialog.garantia.id}/sustituir`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          },
          body: JSON.stringify({ categoria: sustCategoria, garantia: sustCampos, motivo: sustMotivo })
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo sustituir la garantía');
      }
      setSustituirDialog({ open: false, garantia: null });
      fetchGarantias();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSavingSust(false);
    }
  };


  useEffect(() => {
    fetchGarantias();
    fetchSucursales();
    fetchClientes();
  }, []);

  const fetchGarantias = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/garantias/${localStorage.getItem('idcompany')}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );
      const data = await response.json();
      
      if (data["error"] != "Acceso denegado") {
        setAccessDenied(false);
        setGarantiasData(Array.isArray(data) ? data : []);
        setGarantiasFiltered(Array.isArray(data) ? data : []);
      } else {
        setAccessDenied(true);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching garantias:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSucursales = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/sucursales/${localStorage.getItem('idcompany')}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );
      const data = await response.json();
      setSucursales(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching sucursales:', err);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/clientes/${localStorage.getItem('idcompany')}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );
      const data = await response.json();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching clientes:', err);
    }
  };

  const handleSearchChange = (val) => {
    if (val != null) {
      const filtered = garantiasData.filter(g => g.id === val.id);
      setGarantiasFiltered(filterCategory 
        ? filtered.filter(g => g.categoria === filterCategory) 
        : filtered
      );
    } else {
      setGarantiasFiltered(filterCategory 
        ? garantiasData.filter(g => g.categoria === filterCategory) 
        : garantiasData
      );
    }
  };

  const handleCategoryFilter = (categoria) => {
    if (filterCategory === categoria) {
      setFilterCategory(null);
      setGarantiasFiltered(garantiasData);
    } else {
      setFilterCategory(categoria);
      setGarantiasFiltered(garantiasData.filter(g => g.categoria === categoria));
    }
  };

  const applyFilters = () => {
    let filtered = [...garantiasData];

    // Category filter
    if (filters.categoria) {
      filtered = filtered.filter(g => g.categoria === filters.categoria);
    }

    // Estado filter
    if (filters.estado) {
      filtered = filtered.filter(g => (g.estado || 'activa') === filters.estado);
    }

    // Sucursal filter
    if (filters.sucursal) {
      filtered = filtered.filter(g => g.sucursal_nombre === filters.sucursal);
    }

    // Cliente filter
    if (filters.cliente) {
      filtered = filtered.filter(g => g.cliente_nombre?.toLowerCase().includes(filters.cliente.toLowerCase()));
    }

    // Valor range filters
    if (filters.valorMin) {
      filtered = filtered.filter(g => {
        try {
          const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
          return parseFloat(garantiaData.valor || 0) >= parseFloat(filters.valorMin);
        } catch {
          return false;
        }
      });
    }

    if (filters.valorMax) {
      filtered = filtered.filter(g => {
        try {
          const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
          return parseFloat(garantiaData.valor || 0) <= parseFloat(filters.valorMax);
        } catch {
          return true;
        }
      });
    }

    // Date filters
    if (filters.fechaDesde) {
      filtered = filtered.filter(g => {
        if (!g.fecha_creacion) return false;
        const fechaGarantia = new Date(g.fecha_creacion.split('/').reverse().join('-'));
        const fechaDesde = new Date(filters.fechaDesde);
        return fechaGarantia >= fechaDesde;
      });
    }

    if (filters.fechaHasta) {
      filtered = filtered.filter(g => {
        if (!g.fecha_creacion) return false;
        const fechaGarantia = new Date(g.fecha_creacion.split('/').reverse().join('-'));
        const fechaHasta = new Date(filters.fechaHasta);
        return fechaGarantia <= fechaHasta;
      });
    }

    setGarantiasFiltered(filtered);
    setFilterCategory(null); // Reset category card filter when using advanced filters
  };

  const clearFilters = () => {
    setFilters({
      categoria: '',
      estado: '',
      sucursal: '',
      cliente: '',
      valorMin: '',
      valorMax: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setGarantiasFiltered(garantiasData);
    setFilterCategory(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const removeFilter = (field) => {
    const newFilters = { ...filters, [field]: '' };
    setFilters(newFilters);
    
    // Immediately apply filters after removing
    let filtered = [...garantiasData];
    
    if (newFilters.categoria) {
      filtered = filtered.filter(g => g.categoria === newFilters.categoria);
    }
    if (newFilters.sucursal) {
      filtered = filtered.filter(g => g.sucursal_nombre === newFilters.sucursal);
    }
    if (newFilters.cliente) {
      filtered = filtered.filter(g => g.cliente_nombre?.toLowerCase().includes(newFilters.cliente.toLowerCase()));
    }
    if (newFilters.valorMin) {
      filtered = filtered.filter(g => {
        try {
          const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
          return parseFloat(garantiaData.valor || 0) >= parseFloat(newFilters.valorMin);
        } catch {
          return false;
        }
      });
    }
    if (newFilters.valorMax) {
      filtered = filtered.filter(g => {
        try {
          const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
          return parseFloat(garantiaData.valor || 0) <= parseFloat(newFilters.valorMax);
        } catch {
          return true;
        }
      });
    }
    if (newFilters.fechaDesde) {
      filtered = filtered.filter(g => {
        if (!g.fecha_creacion) return false;
        const fechaGarantia = new Date(g.fecha_creacion.split('/').reverse().join('-'));
        const fechaDesde = new Date(newFilters.fechaDesde);
        return fechaGarantia >= fechaDesde;
      });
    }
    if (newFilters.fechaHasta) {
      filtered = filtered.filter(g => {
        if (!g.fecha_creacion) return false;
        const fechaGarantia = new Date(g.fecha_creacion.split('/').reverse().join('-'));
        const fechaHasta = new Date(newFilters.fechaHasta);
        return fechaGarantia <= fechaHasta;
      });
    }
    
    setGarantiasFiltered(filtered);
  };

  const handleViewDetails = (garantia) => {
    setSelectedGarantia(garantia);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedGarantia(null);
  };

  const renderGarantiaDetails = (garantia) => {
    if (!garantia) return null;
    
    const garantiaData = typeof garantia.garantia === 'string' 
      ? JSON.parse(garantia.garantia) 
      : garantia.garantia;

    return (
      <Box sx={{ mt: 2 }}>
        {Object.entries(garantiaData).map(([key, value]) => (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {key.replace(/_/g, ' ')}
            </Typography>
            <Typography variant="body1" fontWeight="500">
              {typeof value === 'number' && key.includes('valor') 
                ? `$${formatNumberWithCommas(value.toFixed(2))}` 
                : value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(value => value !== '').length;

  // Calculate KPIs
  const totalGarantias = garantiasData.length;
  const garantiasByCategory = {
    vehiculo: garantiasData.filter(g => g.categoria === 'vehiculo').length,
    inmueble: garantiasData.filter(g => g.categoria === 'inmueble').length,
    prendas: garantiasData.filter(g => g.categoria === 'prendas').length,
    otro: garantiasData.filter(g => g.categoria === 'otro').length,
  };

  // Calculate total value (if available in garantia data)
  const totalValue = garantiasData.reduce((acc, g) => {
    try {
      const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
      return acc + (parseFloat(garantiaData.valor) || 0);
    } catch {
      return acc;
    }
  }, 0);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando garantías...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    !accessDenied ?
    <>

      {actionError && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        </Box>
      )}

      {/* Summary Cards */}
      <Box sx={{ px: 2, mb: 3, mt: 2 }}>
        <SectionHeader title="Resumen" />
        <Grid container spacing={1.5}>
          <Grid item size={{ xs: 6, md: 3 }}>
            <StatCard
              label="Total Garantías"
              value={totalGarantias}
              accent="#18586b"
              active={filterCategory === null}
              onClick={() => { setFilterCategory(null); setGarantiasFiltered(garantiasData); }}
            />
          </Grid>
          <Grid item size={{ xs: 6, md: 3 }}>
            <StatCard
              label="Vehículos"
              value={garantiasByCategory.vehiculo}
              accent={CATEGORIA_META.vehiculo.color}
              active={filterCategory === 'vehiculo'}
              onClick={() => handleCategoryFilter('vehiculo')}
            />
          </Grid>
          <Grid item size={{ xs: 6, md: 3 }}>
            <StatCard
              label="Inmuebles"
              value={garantiasByCategory.inmueble}
              accent={CATEGORIA_META.inmueble.color}
              active={filterCategory === 'inmueble'}
              onClick={() => handleCategoryFilter('inmueble')}
            />
          </Grid>
          <Grid item size={{ xs: 6, md: 3 }}>
            <StatCard
              label="Prendas & Otros"
              value={garantiasByCategory.prendas + garantiasByCategory.otro}
              accent={CATEGORIA_META.prendas.color}
              active={filterCategory === 'prendas'}
              onClick={() => handleCategoryFilter('prendas')}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Filter Indicators */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {filterCategory && (
            <Chip 
              label={`Categoría: ${capitalize(filterCategory)}`}
              onDelete={() => {
                setFilterCategory(null);
                setGarantiasFiltered(garantiasData);
              }}
              color="primary"
              size="small"
            />
          )}
          {filters.categoria && (
            <Chip
              label={`Categoría: ${capitalize(filters.categoria)}`}
              onDelete={() => removeFilter('categoria')}
              color="primary"
              size="small"
            />
          )}
          {filters.estado && (
            <Chip
              label={`Estado: ${capitalize(filters.estado)}`}
              onDelete={() => removeFilter('estado')}
              color="primary"
              size="small"
            />
          )}
          {filters.sucursal && (
            <Chip 
              label={`Sucursal: ${filters.sucursal}`}
              onDelete={() => removeFilter('sucursal')}
              color="primary"
              size="small"
            />
          )}
          {filters.cliente && (
            <Chip 
              label={`Cliente: ${filters.cliente}`}
              onDelete={() => removeFilter('cliente')}
              color="primary"
              size="small"
            />
          )}
          {(filters.valorMin || filters.valorMax) && (
            <Chip 
              label={`Valor: $${filters.valorMin || '0'} - $${filters.valorMax || '∞'}`}
              onDelete={() => {
                const newFilters = { ...filters, valorMin: '', valorMax: '' };
                setFilters(newFilters);
                let filtered = [...garantiasData];
                if (newFilters.categoria) filtered = filtered.filter(g => g.categoria === newFilters.categoria);
                if (newFilters.sucursal) filtered = filtered.filter(g => g.sucursal_nombre === newFilters.sucursal);
                if (newFilters.cliente) filtered = filtered.filter(g => g.cliente_nombre?.toLowerCase().includes(newFilters.cliente.toLowerCase()));
                if (newFilters.fechaDesde) {
                  filtered = filtered.filter(g => {
                    if (!g.fecha_creacion) return false;
                    const fechaGarantia = new Date(g.fecha_creacion.split('/').reverse().join('-'));
                    const fechaDesde = new Date(newFilters.fechaDesde);
                    return fechaGarantia >= fechaDesde;
                  });
                }
                if (newFilters.fechaHasta) {
                  filtered = filtered.filter(g => {
                    if (!g.fecha_creacion) return false;
                    const fechaGarantia = new Date(g.fecha_creacion.split('/').reverse().join('-'));
                    const fechaHasta = new Date(newFilters.fechaHasta);
                    return fechaGarantia <= fechaHasta;
                  });
                }
                setGarantiasFiltered(filtered);
              }}
              color="primary"
              size="small"
            />
          )}
          {(filters.fechaDesde || filters.fechaHasta) && (
            <Chip 
              label={`Fecha: ${filters.fechaDesde || '...'} / ${filters.fechaHasta || '...'}`}
              onDelete={() => {
                const newFilters = { ...filters, fechaDesde: '', fechaHasta: '' };
                setFilters(newFilters);
                let filtered = [...garantiasData];
                if (newFilters.categoria) filtered = filtered.filter(g => g.categoria === newFilters.categoria);
                if (newFilters.sucursal) filtered = filtered.filter(g => g.sucursal_nombre === newFilters.sucursal);
                if (newFilters.cliente) filtered = filtered.filter(g => g.cliente_nombre?.toLowerCase().includes(newFilters.cliente.toLowerCase()));
                if (newFilters.valorMin) {
                  filtered = filtered.filter(g => {
                    try {
                      const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
                      return parseFloat(garantiaData.valor || 0) >= parseFloat(newFilters.valorMin);
                    } catch { return false; }
                  });
                }
                if (newFilters.valorMax) {
                  filtered = filtered.filter(g => {
                    try {
                      const garantiaData = typeof g.garantia === 'string' ? JSON.parse(g.garantia) : g.garantia;
                      return parseFloat(garantiaData.valor || 0) <= parseFloat(newFilters.valorMax);
                    } catch { return true; }
                  });
                }
                setGarantiasFiltered(filtered);
              }}
              color="primary"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* Search */}
      <Box sx={{ mt: 2, px: 2 }}>
        <Autocomplete
          options={garantiasData}
          getOptionLabel={(option) => {
            const garantiaData = typeof option.garantia === 'string' 
              ? JSON.parse(option.garantia) 
              : option.garantia;
            const identifier = garantiaData.id || garantiaData.descrip || garantiaData.marca || option.id;
            return `${capitalize(option.categoria)} - ${identifier} - ${option.cliente_nombre || 'N/A'}`;
          }}
          onChange={(e, val) => handleSearchChange(val)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              placeholder="Buscar Garantía por identificador, cliente o categoría" 
              fullWidth 
            />
          )}
          sx={{ mb: 2, bgcolor: "#f0f2f5", borderRadius: 2 }}
          fullWidth
        />
      </Box>

      {/* Advanced Filters Button - Aligned Right */}
      <Box sx={{ px: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Badge 
          badgeContent={activeFilterCount} 
          color="primary"
          sx={{ 
            '& .MuiBadge-badge': { 
              right: -3,
              top: 13,
            } 
          }}
        >
          <Button
            startIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            endIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
            size="medium"
          >
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros Avanzados'}
          </Button>
        </Badge>
      </Box>

      {/* Advanced Filters Panel */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Collapse in={showFilters}>
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
            <Grid container spacing={2}>
              {/* Category Filter */}
              <Grid item size={4} xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    sx={{backgroundColor: 'white'}}
                    value={filters.categoria}
                    label="Categoría"
                    onChange={(e) => handleFilterChange('categoria', e.target.value)}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="vehiculo">Vehículo</MenuItem>
                    <MenuItem value="inmueble">Inmueble</MenuItem>
                    <MenuItem value="prendas">Prendas</MenuItem>
                    <MenuItem value="otro">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Estado Filter */}
              <Grid item size={4} xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    sx={{backgroundColor: 'white'}}
                    value={filters.estado}
                    label="Estado"
                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="activa">Activa</MenuItem>
                    <MenuItem value="liberada">Liberada</MenuItem>
                    <MenuItem value="ejecutada">Ejecutada</MenuItem>
                    <MenuItem value="sustituida">Sustituida</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Sucursal Filter */}
              <Grid item size={4} xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sucursal</InputLabel>
                  <Select
                    sx={{backgroundColor: 'white'}}
                    value={filters.sucursal}
                    label="Sucursal"
                    onChange={(e) => handleFilterChange('sucursal', e.target.value)}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {sucursales.map((sucursal) => (
                      <MenuItem key={sucursal.id} value={sucursal.nombre}>
                        {sucursal.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cliente Search */}
              <Grid item size={4} xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar Cliente"
                  sx={{backgroundColor: 'white'}}
                  value={filters.cliente}
                  onChange={(e) => handleFilterChange('cliente', e.target.value)}
                  placeholder="Nombre del cliente"
                />
              </Grid>

              {/* Valor Mínimo */}
              <Grid item size={3} xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Valor Mínimo"
                  sx={{backgroundColor: 'white'}}
                  value={filters.valorMin}
                  onChange={(e) => handleFilterChange('valorMin', e.target.value)}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>

              {/* Valor Máximo */}
              <Grid item size={3} xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Valor Máximo"
                  sx={{backgroundColor: 'white'}}
                  value={filters.valorMax}
                  onChange={(e) => handleFilterChange('valorMax', e.target.value)}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>

              {/* Fecha Desde */}
              <Grid item size={3} xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha Desde"
                  sx={{backgroundColor: 'white'}}
                  value={filters.fechaDesde}
                  onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Fecha Hasta */}
              <Grid item size={3} xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha Hasta"
                  sx={{backgroundColor: 'white'}}
                  value={filters.fechaHasta}
                  onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item size={12} xs={12}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={clearFilters}
                  >
                    Limpiar Filtros
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<FilterList />}
                    onClick={applyFilters}
                  >
                    Aplicar Filtros
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      {/* Table */}
      <Box sx={{ px: 2, mt: 1 }}>
        <SectionHeader title="Listado de garantías" />
      </Box>
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', px: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledCellHeader>ID</StyledCellHeader>
              <StyledCellHeader>Categoría</StyledCellHeader>
              <StyledCellHeader>Estado</StyledCellHeader>
              <StyledCellHeader>Cliente</StyledCellHeader>
              <StyledCellHeader>Préstamo</StyledCellHeader>
              <StyledCellHeader>Detalle Principal</StyledCellHeader>
              <StyledCellHeader>Valor Estimado</StyledCellHeader>
              <StyledCellHeader>Sucursal</StyledCellHeader>
              <StyledCellHeader>Fecha de Registro</StyledCellHeader>
              <StyledCellHeader>Acciones</StyledCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {garantiasFiltered.length > 0 ? (
              garantiasFiltered.map((garantia) => {
                const garantiaData = typeof garantia.garantia === 'string' 
                  ? JSON.parse(garantia.garantia) 
                  : garantia.garantia;
                const mainDetail = garantiaData.id || garantiaData.marca || garantiaData.descrip || 'N/A';
                const valor = parseFloat(garantiaData.valor || 0);
                const estado = garantia.estado || 'activa';
                // El préstamo en 'irrecuperable' sugiere ejecutar la garantía activa
                const sugerirEjecucion = garantia.prestamo_estado === 'irrecuperable' && estado === 'activa';

                return (
                  <TableRow
                    key={garantia.id}
                    hover
                    sx={sugerirEjecucion ? { backgroundColor: '#fdecea' } : undefined}
                  >
                    <TableCell>{garantia.id.substring(0, 8)}...</TableCell>
                    <TableCell>
                      <CategoryChip categoria={garantia.categoria} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <EstadoChip estado={estado} />
                        {sugerirEjecucion && (
                          <Tooltip title="Préstamo irrecuperable: se sugiere ejecutar la garantía">
                            <WarningAmberOutlined fontSize="small" color="error" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{garantia.cliente_nombre || 'N/A'}</TableCell>
                    <TableCell>{garantia.loan_id || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{mainDetail}</TableCell>
                    <TableCell>
                      {valor > 0 ? (
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          ${formatNumberWithCommas(valor.toFixed(2))}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No especificado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{garantia.sucursal_nombre || 'N/A'}</TableCell>
                    <TableCell>{garantia.fecha_creacion || 'N/A'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Ver detalles">
                          <IconButton
                            size='small'
                            color="primary"
                            onClick={() => handleViewDetails(garantia)}
                          >
                            <RemoveRedEyeOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Documentos y fotos">
                          <IconButton size='small' onClick={() => setAdjuntosTarget(garantia)}>
                            <Badge badgeContent={garantia.adjuntos_count || 0} color="primary">
                              <AttachFileOutlined fontSize="small" />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                        {puedeEditar && estado === 'activa' && (
                          <>
                            <Tooltip title="Liberar garantía">
                              <IconButton size='small' color="success" onClick={() => openEstadoDialog(garantia, 'liberada')}>
                                <LockOpenOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ejecutar garantía">
                              <IconButton size='small' color="error" onClick={() => openEstadoDialog(garantia, 'ejecutada')}>
                                <GavelOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sustituir garantía">
                              <IconButton size='small' color="warning" onClick={() => openSustituir(garantia)}>
                                <SwapHorizOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {puedeEditar && estado !== 'activa' && (
                          <Tooltip title="Reactivar garantía">
                            <IconButton size='small' color="info" onClick={() => openEstadoDialog(garantia, 'activa')}>
                              <ReplayOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {puedeEliminar && (
                          <Tooltip title="Eliminar garantía">
                            <IconButton size='small' color="default" onClick={() => setDeleteTarget(garantia)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box sx={{ py: 6 }}>
                    <Diamond sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No se encontraron garantías
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filterCategory 
                        ? `No hay garantías de tipo ${capitalize(filterCategory)} registradas` 
                        : 'Comienza creando préstamos con garantías'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Footer */}
      {garantiasFiltered.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mx: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Mostrando {garantiasFiltered.length} de {garantiasData.length} garantías
                {(filterCategory || activeFilterCount > 0) && (
                  <Chip 
                    label={activeFilterCount > 0 ? `${activeFilterCount} filtros activos` : 'Filtro activo'} 
                    size="small" 
                    color="info"
                    sx={{ ml: 1, height: 20 }}
                  />
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
              <Typography variant="body2" color="text.secondary">
                Valor Total Estimado: <strong>${formatNumberWithCommas(totalValue.toFixed(2))}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog 
        open={openDetailDialog} 
        onClose={handleCloseDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {selectedGarantia && <CategoryChip categoria={selectedGarantia.categoria} />}
              <Typography variant="h6" sx={{ ml: 2 }}>
                Detalles de Garantía
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetailDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedGarantia && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  ID de Garantía
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {selectedGarantia.id}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {selectedGarantia.cliente_nombre || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Sucursal
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {selectedGarantia.sucursal_nombre || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Estado
                </Typography>
                <EstadoChip estado={selectedGarantia.estado || 'activa'} />
                {(selectedGarantia.estado && selectedGarantia.estado !== 'activa') && (
                  <Box sx={{ mt: 1 }}>
                    {selectedGarantia.fecha_estado && (
                      <Typography variant="body2" color="text.secondary">
                        Desde: {selectedGarantia.fecha_estado}
                      </Typography>
                    )}
                    {selectedGarantia.motivo_estado && (
                      <Typography variant="body2" color="text.secondary">
                        Motivo: {selectedGarantia.motivo_estado}
                      </Typography>
                    )}
                    {selectedGarantia.sustituida_por && (
                      <Typography variant="body2" color="text.secondary">
                        Sustituida por: {garantiaLabel(garantiasData.find(g => g.id === selectedGarantia.sustituida_por))}
                      </Typography>
                    )}
                  </Box>
                )}
                {(() => {
                  const predecesora = garantiasData.find(g => g.sustituida_por === selectedGarantia.id);
                  return predecesora ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Sustituye a: {garantiaLabel(predecesora)}
                    </Typography>
                  ) : null;
                })()}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
                Información de la Garantía
              </Typography>

              {renderGarantiaDetails(selectedGarantia)}

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Fecha de Registro
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {selectedGarantia.fecha_creacion || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedGarantia?.solicitud_id && (
            <Button 
              variant="contained" 
              onClick={() => {
                navigate(`/gestion_comercial/prestamos/solicitudes/detalle/${selectedGarantia.solicitud_id}`);
                handleCloseDetailDialog();
              }}
            >
              Ver Solicitud de Préstamo
            </Button>
          )}
          <Button onClick={handleCloseDetailDialog}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Estado change dialog */}
      <Dialog open={estadoDialog.open} onClose={closeEstadoDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {estadoDialog.nuevoEstado && (ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.label || 'Cambiar estado')} garantía
        </DialogTitle>
        <Divider />
        <DialogContent>
          {estadoDialog.garantia && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {`¿Confirmas ${ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.verbo || 'cambiar'} la garantía de `}
              <strong>{estadoDialog.garantia.cliente_nombre || 'N/A'}</strong>
              {` (${capitalize(estadoDialog.garantia.categoria)})?`}
            </Typography>
          )}
          <TextField
            label="Motivo (opcional)"
            placeholder="Detalle del motivo del cambio de estado"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEstadoDialog} disabled={savingEstado}>Cancelar</Button>
          <Button
            variant="contained"
            color={ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.color || 'primary'}
            onClick={submitEstado}
            disabled={savingEstado}
            startIcon={savingEstado ? <CircularProgress size={16} color="inherit" /> : ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.icon}
          >
            {ESTADO_ACCIONES[estadoDialog.nuevoEstado]?.label || 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar garantía</DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Esta acción no se puede deshacer.
          </Alert>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary">
              {`Se eliminará la garantía (${capitalize(deleteTarget.categoria)}) de `}
              <strong>{deleteTarget.cliente_nombre || 'N/A'}</strong>.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Substitution dialog: register the replacement collateral */}
      <Dialog open={sustituirDialog.open} onClose={closeSustituir} maxWidth="sm" fullWidth>
        <DialogTitle>Sustituir garantía</DialogTitle>
        <Divider />
        <DialogContent>
          {sustituirDialog.garantia && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {`La garantía actual (${garantiaLabel(sustituirDialog.garantia)}) quedará como sustituida. `}
              Registra abajo la garantía que la reemplaza.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item size={12} xs={12}>
              <Typography fontWeight={600} color="textSecondary" gutterBottom>Categoría</Typography>
              <Selector
                options={categoriasGarantia}
                value={sustCategoria}
                onChange={(e) => { setSustCampos({}); setSustErrors({}); setSustCategoria(e.target.value); }}
                placeholder="Categoría de garantía"
              />
              {sustErrors.categoria && (
                <Typography variant="caption" color="error">{sustErrors.categoria}</Typography>
              )}
            </Grid>
            {sustCategoria && camposDeCategoria(sustCategoria).map(campo => (
              <Grid item size={6} xs={12} sm={6} key={campo.id}>
                <Typography fontWeight={600} color="textSecondary" gutterBottom>{campo.name}</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={campo.type === 'monto'
                    ? formatNumberWithCommas(sustCampos[campo.id] || '')
                    : (sustCampos[campo.id] || '')}
                  onChange={(e) => handleSustField(campo, e.target.value)}
                  placeholder={campo.name}
                  required={campo.required}
                  error={Boolean(sustErrors[campo.id])}
                  helperText={sustErrors[campo.id]}
                />
              </Grid>
            ))}
            <Grid item size={12} xs={12}>
              <Typography fontWeight={600} color="textSecondary" gutterBottom>Motivo (opcional)</Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder="Motivo de la sustitución"
                value={sustMotivo}
                onChange={(e) => setSustMotivo(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSustituir} disabled={savingSust}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={submitSustituir}
            disabled={savingSust}
            startIcon={savingSust ? <CircularProgress size={16} color="inherit" /> : <SwapHorizOutlined />}
          >
            Sustituir
          </Button>
        </DialogActions>
      </Dialog>

      <GarantiaAdjuntos
        open={Boolean(adjuntosTarget)}
        garantia={adjuntosTarget}
        canEdit={puedeEditar}
        canDelete={puedeEliminar}
        onClose={() => setAdjuntosTarget(null)}
        onChanged={fetchGarantias}
      />
    </>
    :
    <AccessDeniedBox
      title={'Acceso denegado'}
      message={'No tienes permisos para ver esta sección de Garantías. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
    />
  );
};

export default GarantiasManagement;

